import React, { useState } from "react";

import { faPlus } from "@fortawesome/pro-regular-svg-icons";

import { DropdownItem } from "src/design-system/Dropdown";
import { Icon } from "src/design-system/Icon";
import { useModal } from "src/design-system/Modal";
import { CompareModelsModal } from "src/genai/ModelSelector/CompareModelsModal";
import { ModelSelectorDropdown } from "src/genai/ModelSelector/ModelSelectorDropdown";
import {
  ModelOption,
  ProviderFilter,
  RegionFilter,
} from "src/genai/ModelSelector/types";
import { UpgradeNudge } from "src/genai/ModelSelector/UpgradeNudge";

export type ModelPickerProps = {
  /** Models with their rich comparison metadata. */
  models: ModelOption[];
  selectedModelId: string | undefined;
  onSelect: (modelId: string) => void;
  disabled?: boolean;
  isReadOnly?: boolean;
  /** Show the "superseded by …" nudge when the selection has a successor. */
  showUpgradeNudge?: boolean;
  /**
   * Friendly label shown when the selection matches no model (removed model /
   * deleted BYOK credential). Falls back to the raw id.
   */
  missingModelDisplayName?: string;
  /** Initial residency filter applied in the Compare modal. */
  defaultRegion?: RegionFilter;
  /** Initial provider filter applied in the Compare modal. */
  defaultProvider?: ProviderFilter;
  /** data-loc prefix for testing. */
  dataLoc?: string;
  /**
   * The current selection can't be resolved to an option yet because the model
   * list is still loading. Shows a neutral skeleton in the trigger instead of
   * an "Unavailable" pill until the list settles.
   */
  isResolvingSelection?: boolean;
};

/**
 * Resolve the recommended successor option for the nudge. `successorId` from the
 * catalog is a bare `model_id`, but BYOK options use a compound
 * `model_id:byok:credential` id. So for a BYOK selection we only suggest a BYOK
 * successor on the same credential; for a shared selection we match the bare id.
 */
const resolveSuccessor = (
  models: ModelOption[],
  selected: ModelOption | undefined,
): ModelOption | undefined => {
  if (selected?.successorId == null) return undefined;
  const successorId = selected.byok
    ? `${selected.successorId}:byok:${selected.id.split(":byok:")[1]}`
    : selected.successorId;
  return models.find((model) => model.id === successorId);
};

/**
 * LLM model picker: a simple sectioned dropdown (model rows + residency badges)
 * with a pinned "Compare models" footer that opens a rich comparison modal, plus
 * an upgrade nudge when the selection is superseded.
 */
export const ModelPicker: React.FC<ModelPickerProps> = ({
  models,
  selectedModelId,
  onSelect,
  disabled = false,
  isReadOnly = false,
  showUpgradeNudge = true,
  missingModelDisplayName,
  defaultRegion = "any",
  defaultProvider = "any",
  dataLoc = "model-picker",
  isResolvingSelection = false,
}) => {
  const { isOpen, isVisible, openModal, closeModal, afterLeave } = useModal();
  const [nudgeDismissed, setNudgeDismissed] = useState(false);

  // Reset the dismissal whenever the selection changes
  const [lastSelectedModelId, setLastSelectedModelId] =
    useState(selectedModelId);
  if (selectedModelId !== lastSelectedModelId) {
    setLastSelectedModelId(selectedModelId);
    setNudgeDismissed(false);
  }

  const selectedModel = models.find((model) => model.id === selectedModelId);
  const successor = resolveSuccessor(models, selectedModel);

  const selectModel = (modelId: string) => {
    onSelect(modelId);
    setNudgeDismissed(false);
  };

  const showNudge = Boolean(
    showUpgradeNudge &&
      selectedModel &&
      successor &&
      !nudgeDismissed &&
      (isReadOnly || !successor.disabled),
  );

  return (
    <div className="w-full" data-loc={`${dataLoc}-root`}>
      <ModelSelectorDropdown
        dataLoc={dataLoc}
        disabled={disabled || isReadOnly}
        footer={
          // "Compare models" is an action, not a model choice, so it reuses the
          // shared DropdownItem row (grey, hover-highlighted) with a plus icon,
          // rather than a link-styled button. -mx-4 cancels the footer's
          // horizontal padding so the row is full-bleed and aligns with the
          // option rows above; -mb-2 cancels the panel's bottom padding.
          <div className="-mx-4 -mb-2">
            <DropdownItem
              data-loc={`${dataLoc}-compare-link`}
              role="button"
              tabIndex={0}
              onClick={() => openModal()}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  openModal();
                }
              }}
            >
              <span className="flex items-center gap-x-2">
                <Icon icon={faPlus} size="sm" />
                Compare models
              </span>
            </DropdownItem>
          </div>
        }
        isResolvingSelection={isResolvingSelection}
        missingModelDisplayName={missingModelDisplayName}
        modelOptions={models}
        selectedModelId={selectedModelId}
        onSelect={selectModel}
      />

      {showNudge && selectedModel && successor && (
        <UpgradeNudge
          action={
            isReadOnly
              ? { text: "Dismiss", onClick: () => setNudgeDismissed(true) }
              : {
                  text: "Switch model",
                  onClick: () => selectModel(successor.id),
                }
          }
          model={selectedModel}
          successor={successor}
        />
      )}

      {isVisible && (
        <CompareModelsModal
          afterLeave={afterLeave}
          dataLoc={`${dataLoc}-compare`}
          defaultProvider={defaultProvider}
          defaultRegion={defaultRegion}
          isOpen={isOpen}
          isReadOnly={isReadOnly}
          models={models}
          selectedModelId={selectedModelId}
          onClose={closeModal}
          onSelect={selectModel}
        />
      )}
    </div>
  );
};
